/**
 * Thin stable color access via theme catalog (replaces ColorSchemaManager).
 */

import { useMemo } from 'react'
import {
  getThemeCatalogEntry,
  resolveColorTheme,
} from '@/shared/theme/catalog'
import type { ColorTheme, ResolvedTheme } from '@/shared/types/theme.types'

export interface StableColorSchema {
  id: string
  name: string
  variant: ResolvedTheme
  primary: string
  success: string
  error: string
  background: string
  text: string
}

export interface UseStableColorSchemaReturn {
  schema: StableColorSchema
  colors: {
    primary: string
    primaryLight: string
    primaryDark: string
    success: string
    warning: string
    error: string
    info: string
    background: string
    cardBackground: string
    textPrimary: string
    textSecondary: string
    border: string
    accent: string
  }
}

export function useStableColorSchema(
  colorTheme: ColorTheme | string = 'mechagora',
  variant: ResolvedTheme = 'dark'
): UseStableColorSchemaReturn {
  return useMemo(() => {
    const id = resolveColorTheme(colorTheme)
    const entry = getThemeCatalogEntry(id)
    const def = variant === 'dark' ? entry.dark : entry.light
    const c = def.colors
    const schema: StableColorSchema = {
      id: entry.id,
      name: entry.name,
      variant,
      primary: c.primary.primary,
      success: c.semantic.success,
      error: c.semantic.error,
      background: c.surface.background,
      text: c.text.textPrimary,
    }
    return {
      schema,
      colors: {
        primary: c.primary.primary,
        primaryLight: c.primary.primaryLight,
        primaryDark: c.primary.primaryDark,
        success: c.semantic.success,
        warning: c.semantic.warning,
        error: c.semantic.error,
        info: c.semantic.info,
        background: c.surface.background,
        cardBackground: c.surface.card,
        textPrimary: c.text.textPrimary,
        textSecondary: c.text.textSecondary,
        border: c.border.border,
        accent: c.border.accent,
      },
    }
  }, [colorTheme, variant])
}

export default useStableColorSchema
